/**
 * WebSocketService
 *
 * Singleton WebSocket service that connects to Triton Whirligig
 * through the solana-ws-proxy edge function. Manages subscriptions for
 * real-time Meteora DBC trade streaming.
 *
 * Architecture:
 *   Browser → solana-ws-proxy (Supabase Edge) → Triton Whirligig
 *
 * Usage:
 *   const service = WebSocketService.getInstance();
 *   service.connect(wsProxyUrl);
 *   const unsub = service.subscribeGlobal((trade) => { ... });
 *   // later: unsub();
 */

import type {
  TradeEvent,
  TokenInfo,
  Subscription,
  TransactionNotificationResult,
} from './types';
import { METEORA_DBC_PROGRAM_ID } from './types';
import { parseTransactionNotification } from './tradeParser';
import { captureError, addBreadcrumb } from '@/lib/sentry';

/** Per-listener tracking within a deduplicated account subscription */
interface AccountListener {
  id: number;
  callback: () => void;
}

/** Deduplicated account subscription: one upstream RPC per unique address */
interface DedupedAccountSubscription {
  address: string;
  subId: number | null;
  requestId: number;
  listeners: Map<number, AccountListener>;
}

const DEBUG = import.meta.env.DEV;
const MAX_RECONNECT_ATTEMPTS = 10;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
type StatusListener = (status: ConnectionStatus) => void;

export class WebSocketService {
  private static instance: WebSocketService | null = null;

  private ws: WebSocket | null = null;
  private wsUrl: string = '';
  private subscriptions = new Map<number, Subscription>();
  private accountSubsByAddress = new Map<string, DedupedAccountSubscription>();
  private listenerIdToAddress = new Map<number, string>();
  private subIdToRequestId = new Map<number, number>();
  private nextId = 1;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private isConnecting = false;
  private pendingMessages: string[] = [];
  private tokenInfoCache = new Map<string, TokenInfo>();
  private statusListeners = new Set<StatusListener>();
  private _status: ConnectionStatus = 'disconnected';
  private lastMessageTime: number = 0;

  private constructor() {}

  static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  // ---- Token Info Cache ----

  setTokenInfo(mint: string, info: TokenInfo): void {
    this.tokenInfoCache.set(mint, info);
  }

  setTokenInfoBatch(entries: Array<{ mint: string; info: TokenInfo }>): void {
    for (const { mint, info } of entries) {
      this.tokenInfoCache.set(mint, info);
    }
  }

  getTokenInfo(mint: string): TokenInfo | undefined {
    return this.tokenInfoCache.get(mint);
  }

  // ---- Status ----

  get status(): ConnectionStatus {
    return this._status;
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get lastMessageAt(): number {
    return this.lastMessageTime;
  }

  isStale(thresholdMs = 60_000): boolean {
    if (!this.connected) return false;
    if (this.lastMessageTime === 0) return false;
    return Date.now() - this.lastMessageTime > thresholdMs;
  }

  forceReconnect(): void {
    if (DEBUG) console.log('[WS] Force reconnect triggered');

    // Clear any pending reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Reset attempts so reconnect doesn't hit the limit
    this.reconnectAttempts = 0;

    // Null out handlers before close to prevent scheduleReconnect from firing
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      try {
        this.ws.close(1000, 'Force reconnect');
      } catch { /* ignore */ }
      this.ws = null;
    }

    this.isConnecting = false;
    this.setStatus('connecting');

    // Reconnect immediately using the stored URL
    if (this.wsUrl) {
      this.connect(this.wsUrl);
    }
  }

  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this._status === status) return;
    this._status = status;
    for (const listener of this.statusListeners) {
      try {
        listener(status);
      } catch { /* ignore listener errors */ }
    }
  }

  // ---- Connection Management ----

  connect(wsUrl: string): void {
    if (this.ws?.readyState === WebSocket.OPEN && this.wsUrl === wsUrl) return;
    if (this.isConnecting) return;

    this.wsUrl = wsUrl;
    this.isConnecting = true;
    this.setStatus('connecting');

    if (DEBUG) console.log('[WS] Connecting to', wsUrl.split('?')[0]);

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (err) {
      if (DEBUG) console.error('[WS] Failed to create WebSocket:', err);
      // Note: WS errors are sampled at 1% via beforeSend filter
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'websocket_creation',
        wsUrl: wsUrl.split('?')[0],
      });
      this.isConnecting = false;
      this.setStatus('error');
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.setStatus('connected');

      if (DEBUG) console.log('[WS] Connected');

      // Flush pending messages
      for (const msg of this.pendingMessages) {
        this.ws?.send(msg);
      }
      this.pendingMessages = [];

      // Resubscribe existing transaction subscriptions
      for (const [, sub] of this.subscriptions) {
        sub.subId = null;
        this.sendSubscribe(sub);
      }

      // Resubscribe existing account subscriptions (deduplicated: one per address)
      for (const [, deduped] of this.accountSubsByAddress) {
        deduped.subId = null;
        deduped.requestId = this.nextId++;
        this.sendDedupedAccountSubscribe(deduped);
      }
    };

    this.ws.onmessage = (event) => {
      this.lastMessageTime = Date.now();
      this.handleMessage(event.data as string);
    };

    this.ws.onclose = (event) => {
      if (DEBUG) console.log(`[WS] Disconnected: ${event.code} ${event.reason}`);
      this.isConnecting = false;
      this.setStatus('disconnected');
      this.scheduleReconnect();
    };

    this.ws.onerror = (error) => {
      if (DEBUG) console.error('[WS] Error:', error);
      this.isConnecting = false;
      this.setStatus('error');
      captureError(new Error('WebSocket connection error'), {
        context: 'websocket',
        wsUrl: wsUrl.split('?')[0],
      });
    };
  }

  disconnect(): void {
    if (DEBUG) console.log('[WS] Disconnecting');

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.reconnectAttempts = MAX_RECONNECT_ATTEMPTS; // Prevent reconnect

    if (this.ws) {
      try {
        this.ws.close(1000, 'Client disconnect');
      } catch { /* ignore */ }
      this.ws = null;
    }

    this.subscriptions.clear();
    this.accountSubsByAddress.clear();
    this.listenerIdToAddress.clear();
    this.subIdToRequestId.clear();
    this.pendingMessages = [];
    this.setStatus('disconnected');
  }

  // ---- Subscription Management ----

  /**
   * Subscribe to all Meteora DBC program transactions (global trade stream).
   * Returns an unsubscribe function.
   */
  subscribeGlobal(callback: (trade: TradeEvent) => void): () => void {
    const id = this.nextId++;
    const sub: Subscription = {
      id,
      subId: null,
      accounts: [METEORA_DBC_PROGRAM_ID],
      callback,
      type: 'global',
    };

    this.subscriptions.set(id, sub);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendSubscribe(sub);
    }

    return () => this.removeSubscription(id);
  }

  /**
   * Subscribe to transactions for a specific pool address (per-token stream).
   * Returns an unsubscribe function.
   */
  subscribePool(poolAddress: string, callback: (trade: TradeEvent) => void): () => void {
    const id = this.nextId++;
    const sub: Subscription = {
      id,
      subId: null,
      accounts: [poolAddress],
      callback,
      type: 'pool',
    };

    this.subscriptions.set(id, sub);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendSubscribe(sub);
    }

    return () => this.removeSubscription(id);
  }

  /**
   * Subscribe to account changes for a specific address (e.g. pool account for bonding curve).
   * Uses standard Solana accountSubscribe. Returns an unsubscribe function.
   *
   * Deduplicated: multiple subscribers to the same address share ONE upstream
   * accountSubscribe RPC. Notifications are fanned out to all listeners.
   */
  subscribeAccount(address: string, callback: () => void): () => void {
    const listenerId = this.nextId++;

    const existing = this.accountSubsByAddress.get(address);
    if (existing) {
      existing.listeners.set(listenerId, { id: listenerId, callback });
      this.listenerIdToAddress.set(listenerId, address);

      if (DEBUG) {
        console.log(`[WS] accountSubscribe reuse (listenerId=${listenerId}, address=${address.slice(0, 8)}..., listeners=${existing.listeners.size})`);
        this.logSubscriptionSummary();
      }

      return () => this.removeAccountListener(listenerId);
    }

    const requestId = this.nextId++;
    const deduped: DedupedAccountSubscription = {
      address,
      subId: null,
      requestId,
      listeners: new Map([[listenerId, { id: listenerId, callback }]]),
    };
    this.accountSubsByAddress.set(address, deduped);
    this.listenerIdToAddress.set(listenerId, address);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendDedupedAccountSubscribe(deduped);
    }

    if (DEBUG) this.logSubscriptionSummary();

    return () => this.removeAccountListener(listenerId);
  }

  private sendDedupedAccountSubscribe(sub: DedupedAccountSubscription): void {
    const msg = JSON.stringify({
      jsonrpc: '2.0',
      id: sub.requestId,
      method: 'accountSubscribe',
      params: [
        sub.address,
        { encoding: 'base64', commitment: 'confirmed' },
      ],
    });

    if (DEBUG) console.log(`[WS] accountSubscribe (requestId=${sub.requestId}, address=${sub.address.slice(0, 8)}..., listeners=${sub.listeners.size})`);
    this.send(msg);
  }

  private removeAccountListener(listenerId: number): void {
    const address = this.listenerIdToAddress.get(listenerId);
    if (!address) return;

    this.listenerIdToAddress.delete(listenerId);

    const deduped = this.accountSubsByAddress.get(address);
    if (!deduped) return;

    deduped.listeners.delete(listenerId);

    if (deduped.listeners.size === 0) {
      if (deduped.subId != null && this.ws?.readyState === WebSocket.OPEN) {
        const msg = JSON.stringify({
          jsonrpc: '2.0',
          id: this.nextId++,
          method: 'accountUnsubscribe',
          params: [deduped.subId],
        });
        this.send(msg);
        this.subIdToRequestId.delete(deduped.subId);
      }
      this.accountSubsByAddress.delete(address);
    }

    if (DEBUG) this.logSubscriptionSummary();
  }

  private removeSubscription(id: number): void {
    const sub = this.subscriptions.get(id);
    if (sub?.subId != null && this.ws?.readyState === WebSocket.OPEN) {
      this.sendUnsubscribe(sub.subId);
    }
    this.subscriptions.delete(id);
  }

  // ---- WebSocket Protocol ----

  private sendSubscribe(sub: Subscription): void {
    const msg = JSON.stringify({
      jsonrpc: '2.0',
      id: sub.id,
      method: 'transactionSubscribe',
      params: [
        {
          vote: false,
          failed: false,
          accountInclude: sub.accounts,
          accountExclude: [],
          accountRequired: [],
        },
        {
          commitment: 'confirmed',
          encoding: 'jsonParsed',
          transactionDetails: 'full',
          showRewards: false,
          maxSupportedTransactionVersion: 0,
        },
      ],
    });

    if (DEBUG) console.log(`[WS] Subscribing (id=${sub.id}, type=${sub.type})`);
    this.send(msg);
  }

  private sendUnsubscribe(subId: number): void {
    const msg = JSON.stringify({
      jsonrpc: '2.0',
      id: this.nextId++,
      method: 'transactionUnsubscribe',
      params: [subId],
    });
    this.send(msg);
    this.subIdToRequestId.delete(subId);
  }

  private send(msg: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(msg);
    } else {
      this.pendingMessages.push(msg);
    }
  }

  // ---- Message Handling ----

  private handleMessage(data: string): void {
    try {
      const msg = JSON.parse(data);

      // Handle subscription confirmation: { id: <requestId>, result: <subscriptionId> }
      if (msg.id != null && msg.result !== undefined && typeof msg.result === 'number') {
        // Check transaction subscriptions first
        const sub = this.subscriptions.get(msg.id);
        if (sub) {
          sub.subId = msg.result;
          this.subIdToRequestId.set(msg.result, msg.id);
          if (DEBUG) console.log(`[WS] Subscription confirmed (id=${msg.id}, subId=${msg.result})`);
          return;
        }

        // Check deduplicated account subscriptions
        for (const [, deduped] of this.accountSubsByAddress) {
          if (deduped.requestId === msg.id) {
            deduped.subId = msg.result;
            this.subIdToRequestId.set(msg.result, deduped.requestId);
            if (DEBUG) console.log(`[WS] Account subscription confirmed (requestId=${msg.id}, subId=${msg.result}, address=${deduped.address.slice(0, 8)}..., listeners=${deduped.listeners.size})`);
            return;
          }
        }
        return;
      }

      // Handle unsubscribe confirmation
      if (msg.id != null && msg.result === true) {
        return;
      }

      // Handle error responses
      if (msg.error) {
        console.error('[WS] RPC error:', msg.error);
        return;
      }

      // Handle transaction notification
      if (msg.method === 'transactionNotification' && msg.params) {
        const subId: number = msg.params.subscription;
        const requestId = this.subIdToRequestId.get(subId);
        if (requestId === undefined) return;

        const sub = this.subscriptions.get(requestId);
        if (!sub) return;

        const result = msg.params.result as TransactionNotificationResult;
        if (!result) return;

        const trade = parseTransactionNotification(result, this.tokenInfoCache);
        if (trade) {
          try {
            sub.callback(trade);
          } catch (err) {
            if (DEBUG) console.error('[WS] Callback error:', err);
            // Note: WS errors are sampled at 1% via beforeSend filter
            captureError(err instanceof Error ? err : new Error(String(err)), {
              context: 'websocket_trade_callback',
            });
          }
        }
        return;
      }

      // Handle account notification (used for bonding curve real-time updates)
      // Fans out to all listeners on the deduplicated subscription
      if (msg.method === 'accountNotification' && msg.params) {
        const subId: number = msg.params.subscription;
        const requestId = this.subIdToRequestId.get(subId);
        if (requestId === undefined) return;

        let targetSub: DedupedAccountSubscription | undefined;
        for (const [, deduped] of this.accountSubsByAddress) {
          if (deduped.requestId === requestId) {
            targetSub = deduped;
            break;
          }
        }
        if (!targetSub) return;

        for (const [, listener] of targetSub.listeners) {
          try {
            listener.callback();
          } catch (err) {
            if (DEBUG) console.error('[WS] Account callback error:', err);
            // Note: WS errors are sampled at 1% via beforeSend filter
            captureError(err instanceof Error ? err : new Error(String(err)), {
              context: 'websocket_account_callback',
            });
          }
        }
      }
    } catch (err) {
      if (DEBUG) console.error('[WS] Message parse error:', err);
      // Note: WS errors are sampled at 1% via beforeSend filter
      captureError(err instanceof Error ? err : new Error(String(err)), {
        context: 'websocket_message_parse',
      });
    }
  }

  // ---- Reconnection ----

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      if (DEBUG) console.log('[WS] Max reconnect attempts reached');
      this.setStatus('error');
      addBreadcrumb('WebSocket max reconnect attempts exceeded', 'websocket', {
        attempts: MAX_RECONNECT_ATTEMPTS,
        wsUrl: this.wsUrl.split('?')[0],
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY_MS,
    );

    if (DEBUG) console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      if (this.wsUrl) {
        this.connect(this.wsUrl);
      }
    }, delay);
  }

  // ---- Dev Logging ----

  private logSubscriptionSummary(): void {
    if (!DEBUG) return;

    const accountParts: string[] = [];
    for (const [address, sub] of this.accountSubsByAddress) {
      accountParts.push(`${address.slice(0, 8)}: ${sub.listeners.size} listener${sub.listeners.size !== 1 ? 's' : ''}`);
    }

    const txnCount = this.subscriptions.size;
    console.log(
      `[WS] Active subscriptions: ${this.accountSubsByAddress.size} accountSubscribe (${accountParts.join(', ')}), ${txnCount} transactionSubscribe`
    );
  }
}
